import {
  Button,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  TimePicker,
  Input,
} from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import ReactQuill from "react-quill-new";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  useAddExamPeriod,
  useEditExamPeriod,
  useGetExamRooms,
  useGetExams,
  useGetAdminUsers,
} from "@hooks";
import { Exam, ExamPeriod, ExamPeriodState, User } from "@types";
import {
  MyFormItem,
  MyFormItemGroup,
} from "../../custom-form-item/CustomFormItem";
import { setExamPeriodFormValues } from "./helpers";
import { useExamStore } from "@store";
import { setTimeWithSeconds } from "../../helpers";
import { useNavigate } from "react-router-dom";
import { convertDayjsToTotalSeconds } from "../../../configurations/helpers";

interface Props {
  setSelectedExamPeriod: React.Dispatch<
    React.SetStateAction<ExamPeriod | undefined>
  >;
  setSelectedExam: React.Dispatch<React.SetStateAction<Exam | undefined>>;
  examPeriod?: ExamPeriod;
  setActiveKey: Dispatch<SetStateAction<string>>;
  examId?: string;
  selectedExam?: Exam;

}
export const ExamPeriodPrimarySettings = ({
  setSelectedExamPeriod,
  setSelectedExam,
  examPeriod,
  setActiveKey,
  examId,
  selectedExam
}: Props) => {
  const [examPeriodForm] = Form.useForm();
  const [showZoom, setShowZoom] = useState(false);
  const { t } = useTranslation();
  const { data: exams, isLoading: examsLoading } = useGetExams();
  const { data: adminUsers, isLoading: adminUsersLoading } = useGetAdminUsers();
  const addExamPeriod = useAddExamPeriod();
  const editExamPeriod = useEditExamPeriod();
  const currentUser = useExamStore((state) => state.currentUser);
  const {data: examRooms } = useGetExamRooms();
  const navigate = useNavigate();

  // const {data: examRooms, isLoading: examRoomsLoading} = useGetExamRooms();

  useEffect(() => {
    if (examPeriod) {
      setExamPeriodFormValues(examPeriodForm, examPeriod);
      setSelectedExam(examPeriod.exam);
      setShowZoom(!!(examPeriod.zoomMeetingId || examPeriod.zoomPasscode));
    }
    if (examId && exams) {
      let exam = exams?.find((exam: Exam) => exam.id === examId);
      if (exam) {
        examPeriodForm.setFieldsValue({
          time: setTimeWithSeconds(exam.timeInSeconds),
          passPercentage: exam.gradingConfigurationId ? undefined : exam.percentageToPass,
        });
        setExamPeriodFormValues(examPeriodForm, exam);
      }
      setSelectedExam(exams?.find((exam: Exam) => exam.id === examId))
      examPeriodForm.setFieldsValue({ exam: { id: examId } });
    } else if (selectedExam) {
      let exam = exams?.find((exam: Exam) => exam.id === selectedExam.id);
      if (exam) {
        examPeriodForm.setFieldsValue({
          time: setTimeWithSeconds(exam.timeInSeconds),
          passPercentage: exam.gradingConfigurationId ? undefined : exam.percentageToPass,
        });
        setExamPeriodFormValues(examPeriodForm, exam);
        examPeriodForm.setFieldsValue({ exam: { id: selectedExam.id } });
      }
    }
  }, [examPeriod, selectedExam, exams]);

  // Clear passPercentage when selected exam has a grading configuration
  useEffect(() => {
    if (selectedExam?.gradingConfigurationId) {
      examPeriodForm.setFieldsValue({ passPercentage: undefined });
    }
  }, [selectedExam?.gradingConfigurationId, examPeriodForm]);

  const onFinish = (values: any) => {
    if (examPeriod) {
      editExamPeriod.mutate(
        {
          ...examPeriod,
          ...values,
          time: convertDayjsToTotalSeconds(values.time),
        },
        {
          onSuccess() {
            if (currentUser?.applicationRoles.some((role) => role.name === "TermAdmin") && currentUser?.applicationRoles.some((role) => role.name === "ExamAdmin")) {
              setActiveKey("2");
            } else {
              setActiveKey("2");
            }
            
          },
        }
      );
    } else {
      addExamPeriod.mutate(
        {
          ...values,
          state: ExamPeriodState.InActive,
          examRoom: { id: examRooms?.[0]?.id },
          time: convertDayjsToTotalSeconds(values.time),
        },
        {
          onSuccess(data) {
            setSelectedExamPeriod(data);
            if (currentUser?.applicationRoles.some((role) => role.name === "TermAdmin") && currentUser?.applicationRoles.some((role) => role.name === "ExamAdmin")) {
              setActiveKey("2");
            } else {
              setActiveKey("2");
            }
          },
        }
      );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      <Form
        layout="vertical"
        form={examPeriodForm}
        onFinish={onFinish}
        style={{ width: "100%" }}
      >
        <Row gutter={16}>
          <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
            <MyFormItemGroup prefix={["exam"]}>
              <MyFormItem
                label={t("pages.examPeriods.primarySettingsTab.chooseExam")}
                name={["id"]}
                rules={[
                  {
                    required: true,
                    message: t(
                      "pages.examPeriods.primarySettingsTab.chooseExam"
                    ),
                  },
                ]}
              >
                <Select
                  loading={examsLoading}
                  optionFilterProp="label"
                  showSearch
                  onChange={(value) => {
                    setSelectedExam(
                      exams?.find((exam: Exam) => exam.id === value)
                    );
                  }}
                  placeholder={t(
                    "pages.examPeriods.primarySettingsTab.chooseExam"
                  )}
                  options={exams?.map((exam: Exam) => ({
                    value: exam.id,
                    label: exam.title,
                  }))}
                />
              </MyFormItem>
            </MyFormItemGroup>

            <Form.Item
              label={t("pages.examPeriods.primarySettingsTab.dateAndTime")}
              name={["date"]}
              rules={[
                {
                  required: true,
                  message: t(
                    "pages.examPeriods.primarySettingsTab.dateAndTime"
                  ),
                },
              ]}
            >
              <DatePicker
                showTime
                format={"D.MM.YYYY H:mm"}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Space>
            <Form.Item
              label={t("pages.examPeriods.primarySettingsTab.safeExam")}
              valuePropName="checked"
              name={["safeExamBrowserEnabled"]}
            >
              <Switch />
            </Form.Item>
            <Form.Item
              valuePropName="checked"
              name={["videoProctoringEnabled"]}
              label={t("pages.examPeriods.primarySettingsTab.videoProctoring") || "Video Proctoring"}
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label="Zoom"
            >
              <Switch
                checked={showZoom}
                onChange={(checked) => {
                  setShowZoom(checked);
                  if (!checked) {
                    examPeriodForm.setFieldsValue({ zoomMeetingId: null, zoomPasscode: null });
                  }
                }}
              />
            </Form.Item>
            </Space>
            {showZoom && (
              <Space>
                <Form.Item
                  label={t("pages.examsManagment.primarySettingsTab.zoomMeetingId")}
                  name={["zoomMeetingId"]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label={t("pages.examsManagment.primarySettingsTab.zoompass")}
                  name={["zoomPasscode"]}
                >
                  <Input />
                </Form.Item>
              </Space>
            )}
          
          </Col>
          <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
            <Space size={"middle"} wrap>
              <Form.Item
                label={t("pages.examPeriods.primarySettingsTab.duration")}
                name={["time"]}
                rules={[
                  {
                    required: true,
                    message: t("pages.examPeriods.primarySettingsTab.duration"),
                  },
                ]}
              >
                <TimePicker
                  minuteStep={5}
                  showNow={false}
                  style={{ width: 120 }}
                  needConfirm={false}
                  format={"H[h] mm[min]"}
                />
              </Form.Item>
              {!selectedExam?.gradingConfigurationId && (
                <Form.Item
                  label={t("pages.examPeriods.primarySettingsTab.percentage")}
                  name={["passPercentage"]}
                  rules={[
                    {
                      required: true,
                      message: t(
                        "pages.examPeriods.primarySettingsTab.percentage"
                      ),
                    },
                  ]}
                >
                  <InputNumber suffix="%" />
                </Form.Item>
              )}
              {/* <Form.Item valuePropName="checked" name={["monitoring"]} label={t("pages.examPeriods.monitoring")}>
                <Switch />
              </Form.Item> */}
              <Form.Item valuePropName="checked" name={["learnerReviewEnabled"]} label={t("pages.examPeriods.primarySettingsTab.learnerReviewEnabled")}>
                <Switch />
              </Form.Item>
              <Form.Item
                name={["allowReturn"]}
                valuePropName="checked"
                label={t("pages.examsManagment.primarySettingsTab.prevQuestion")}
              >
                <Switch />
              </Form.Item>
            </Space>

            <Form.Item
              label={t("pages.examPeriods.primarySettingsTab.contact")}
              name={["contactId"]}
              rules={[
                {
                  // required: true,
                  message: t("pages.examPeriods.primarySettingsTab.contact"),
                },
              ]}
            >
              <Select
                // mode="multiple"
                allowClear
                maxTagCount="responsive"
                loading={adminUsersLoading}
                placeholder={t("pages.examPeriods.primarySettingsTab.contact")}
                options={adminUsers?.items.map((user: User) => ({
                  value: user.id,
                  label: user.firstName + " " + user.lastName,
                }))}
              />
            </Form.Item>

            <Form.Item
              name={["applicantNote"]}
              label={t("pages.examPeriods.primarySettingsTab.note")}
            >
              <ReactQuill theme={"snow"}/>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          style={{
            display: "flex",
            flexDirection: "row-reverse",
          }}
        >
          <Space>
            <Button onClick={() => navigate(-1)}>{t("pages.examsManagment.cancel")}</Button>
            <Button onClick={() => {
                  if (examPeriod) {
                    editExamPeriod.mutate(
                      {
                        ...examPeriod,
                        time: convertDayjsToTotalSeconds(examPeriodForm.getFieldValue("time")),
                      },
                      {
                        onSuccess() {
                          if (currentUser?.applicationRoles.some((role) => role.name === "TermAdmin") && currentUser?.applicationRoles.some((role) => role.name === "ExamAdmin")) {
                            setActiveKey("3");
                          } else {
                            setActiveKey("3");
                          }
                          
                        },
                      }
                    );
                  } else {
                    addExamPeriod.mutate(
                      {
                        ...examPeriodForm.getFieldsValue(),
                        state: ExamPeriodState.InActive,
                        examRoom: { id: examRooms?.[0]?.id },
                        time: convertDayjsToTotalSeconds(examPeriodForm.getFieldValue("time")),
                      },
                      {
                        onSuccess(data) {
                          setSelectedExamPeriod(data);
                          if (currentUser?.applicationRoles.some((role) => role.name === "TermAdmin") && currentUser?.applicationRoles.some((role) => role.name === "ExamAdmin")) {
                            setActiveKey("3");
                          } else {
                            setActiveKey("3");
                          }
                        },
                      }
                    );
                  }
              }} type="dashed" loading={addExamPeriod.isPending || editExamPeriod.isPending} disabled={ examPeriod !== undefined ? examPeriod?.state !== ExamPeriodState.InActive : false }>{t("pages.examsManagment.saveQuit")}</Button>
            <Button
              disabled={ examPeriod !== undefined ? examPeriod?.state !== ExamPeriodState.InActive : false }
              type="primary"
              htmlType="submit"
              loading={
                examPeriod ? editExamPeriod.isPending : addExamPeriod.isPending
              }
            >
              {t("pages.examsManagment.next")}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </motion.div>
  );
};
